import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload, Download, Edit, Trash2, Eye, Search, Filter, FileText, Video, Users, BookOpen, Edit as QuizIcon, X, Link as LinkIcon, Tag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';

const MyMaterials = ({ activeTab = 'materials' }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentUploadTab, setCurrentUploadTab] = useState('class-materials');
  
  const [fileUploadData, setFileUploadData] = useState({
    name: '',
    file: null,
    description: '',
    tags: ''
  });

  const [videoLinkData, setVideoLinkData] = useState({
    name: '',
    videoLink: '',
    description: '',
    tags: ''
  });
  
  const materials = {
    'class-materials': [
      {
        id: 1,
        name: 'Leadership Excellence Slides',
        type: 'PowerPoint',
        size: '2.4 MB',
        lastModified: '2024-03-10',
        tags: ['Leadership', 'Management', 'Workshop'],
        downloads: 45
      },
      {
        id: 2,
        name: 'Team Building Exercise Guide',
        type: 'PDF',
        size: '1.2 MB',
        lastModified: '2024-03-08',
        tags: ['Team Building', 'Activities', 'Group Work'],
        downloads: 32
      },
      {
        id: 3,
        name: 'Digital Transformation Framework',
        type: 'Word Document',
        size: '856 KB',
        lastModified: '2024-03-05',
        tags: ['Digital', 'Transformation', 'Strategy'],
        downloads: 67
      }
    ],
    'reading-materials': [
      {
        id: 4,
        name: 'Harvard Business Review - Leadership',
        type: 'PDF',
        size: '3.2 MB',
        lastModified: '2024-03-12',
        tags: ['Leadership', 'Research', 'Case Study'],
        downloads: 89
      },
      {
        id: 5,
        name: 'Remote Work Best Practices Guide',
        type: 'PDF',
        size: '1.8 MB',
        lastModified: '2024-03-09',
        tags: ['Remote Work', 'Best Practices', 'Guide'],
        downloads: 56
      }
    ],
    'quizzes': [
      {
        id: 6,
        name: 'Leadership Style Assessment',
        type: 'Interactive Quiz',
        questions: 25,
        lastModified: '2024-03-11',
        tags: ['Assessment', 'Leadership', 'Self-Evaluation'],
        completions: 234
      },
      {
        id: 7,
        name: 'Team Dynamics Quiz',
        type: 'Multiple Choice',
        questions: 15,
        lastModified: '2024-03-07',
        tags: ['Team Building', 'Assessment', 'Group Dynamics'],
        completions: 167
      }
    ],
    'ice-breakers': [
      {
        id: 8,
        name: 'Two Truths and a Lie - Professional Edition',
        type: 'Activity Guide',
        duration: '10-15 minutes',
        lastModified: '2024-03-10',
        tags: ['Ice Breaker', 'Team Building', 'Networking'],
        uses: 78
      },
      {
        id: 9,
        name: 'Virtual Coffee Chat Prompts',
        type: 'Discussion Starter',
        duration: '5-10 minutes',
        lastModified: '2024-03-06',
        tags: ['Virtual', 'Ice Breaker', 'Remote'],
        uses: 45
      }
    ],
    'videos': [
      {
        id: 10,
        name: 'Effective Communication Techniques',
        type: 'Video Link',
        duration: '12:30',
        videoLink: 'https://youtube.com/watch?v=example1',
        lastModified: '2024-03-09',
        tags: ['Communication', 'Skills', 'Training'],
        views: 156
      },
      {
        id: 11,
        name: 'Leadership in Crisis Demo',
        type: 'Video Link',
        duration: '8:45',
        videoLink: 'https://youtube.com/watch?v=example2',
        lastModified: '2024-03-12',
        tags: ['Leadership', 'Crisis Management', 'Demo'],
        views: 203
      }
    ]
  };

  const handleTabClick = (tabKey) => {
    // Trigger a custom event that BusinessHub can listen to
    // Or we can dispatch an event to update parent state
    window.dispatchEvent(new CustomEvent('material-tab-change', { 
      detail: { tabKey } 
    }));
  };

  const handleUploadClick = (tabKey) => {
    setCurrentUploadTab(tabKey);
    setShowUploadModal(true);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileUploadData(prev => ({ ...prev, file, name: file.name }));
    }
  };

  const handleFileUploadSubmit = (e) => {
    e.preventDefault();
    console.log('File upload data:', fileUploadData);
    alert('Material uploaded successfully!');
    setShowUploadModal(false);
    resetFileUploadData();
  };

  const handleVideoLinkSubmit = (e) => {
    e.preventDefault();
    console.log('Video link data:', videoLinkData);
    alert('Video link added successfully!');
    setShowUploadModal(false);
    resetVideoLinkData();
  };

  const resetFileUploadData = () => {
    setFileUploadData({
      name: '',
      file: null,
      description: '',
      tags: ''
    });
  };

  const resetVideoLinkData = () => {
    setVideoLinkData({
      name: '',
      videoLink: '',
      description: '',
      tags: ''
    });
  };

  const handlePreview = (material) => {
    alert(`Preview: ${material.name}`);
  };

  const handleDownload = (material) => {
    alert(`Downloading: ${material.name}`);
  };

  const handleEdit = (material) => {
    alert(`Edit: ${material.name}`);
  };

  const handleDelete = (material) => {
    if (confirm(`Are you sure you want to delete "${material.name}"?`)) {
      alert(`Deleted: ${material.name}`);
    }
  };

  const getTabData = (tabId) => {
    return materials[tabId] || [];
  };

  const getTabIcon = (tabId) => {
    switch (tabId) {
      case 'class-materials': return BookOpen;
      case 'reading-materials': return FileText;
      case 'quizzes': return QuizIcon;
      case 'ice-breakers': return Users;
      case 'videos': return Video;
      default: return FileText;
    }
  };

  const getCurrentTab = () => {
    if (activeTab === 'materials') return 'class-materials';
    return activeTab;
  };

  const currentTab = getCurrentTab();
  const currentData = getTabData(currentTab);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>My Training Materials</span>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search materials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => alert('Filter functionality')}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleUploadClick(currentTab)}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Material
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={currentTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger 
                value="class-materials" 
                className="flex items-center space-x-2"
                onClick={() => handleTabClick('class-materials')}
              >
                <BookOpen className="w-4 h-4" />
                <span>Class Materials</span>
              </TabsTrigger>
              <TabsTrigger 
                value="reading-materials" 
                className="flex items-center space-x-2"
                onClick={() => handleTabClick('reading-materials')}
              >
                <FileText className="w-4 h-4" />
                <span>Reading Materials</span>
              </TabsTrigger>
              <TabsTrigger 
                value="quizzes" 
                className="flex items-center space-x-2"
                onClick={() => handleTabClick('quizzes')}
              >
                <QuizIcon className="w-4 h-4" />
                <span>Quizzes</span>
              </TabsTrigger>
              <TabsTrigger 
                value="ice-breakers" 
                className="flex items-center space-x-2"
                onClick={() => handleTabClick('ice-breakers')}
              >
                <Users className="w-4 h-4" />
                <span>Ice Breakers</span>
              </TabsTrigger>
              <TabsTrigger 
                value="videos" 
                className="flex items-center space-x-2"
                onClick={() => handleTabClick('videos')}
              >
                <Video className="w-4 h-4" />
                <span>Videos</span>
              </TabsTrigger>
            </TabsList>

            {Object.keys(materials).map((tabKey) => (
              <TabsContent key={tabKey} value={tabKey} className="space-y-4 mt-6">
                {getTabData(tabKey).length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      {React.createElement(getTabIcon(tabKey), { className: "w-12 h-12 text-gray-400 mx-auto mb-4" })}
                      <h3 className="font-semibold text-lg mb-2">No materials yet</h3>
                      <p className="text-gray-600 mb-4">
                        Upload your first {tabKey.replace('-', ' ')} to get started.
                      </p>
                      <Button 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleUploadClick(tabKey)}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload {tabKey.replace('-', ' ')}
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {getTabData(tabKey).map((material) => (
                      <Card key={material.id} className="border-2 hover:border-green-200 transition-colors">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                {React.createElement(getTabIcon(tabKey), { className: "w-5 h-5 text-green-600" })}
                                <h3 className="text-lg font-semibold">{material.name}</h3>
                                <Badge variant="outline">
                                  {material.type}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4 mb-3">
                                <div className="space-y-1">
                                  {material.size && (
                                    <p className="text-sm text-gray-600">Size: {material.size}</p>
                                  )}
                                  {material.duration && (
                                    <p className="text-sm text-gray-600">Duration: {material.duration}</p>
                                  )}
                                  {material.questions && (
                                    <p className="text-sm text-gray-600">Questions: {material.questions}</p>
                                  )}
                                  {material.videoLink && (
                                    <p className="text-sm text-blue-600">
                                      <LinkIcon className="w-3 h-3 inline mr-1" />
                                      <a href={material.videoLink} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                        Video Link
                                      </a>
                                    </p>
                                  )}
                                  <p className="text-sm text-gray-600">Modified: {material.lastModified}</p>
                                </div>
                                
                                <div className="space-y-1">
                                  {material.downloads && (
                                    <p className="text-sm text-green-600 font-medium">
                                      Downloads: {material.downloads}
                                    </p>
                                  )}
                                  {material.views && (
                                    <p className="text-sm text-blue-600 font-medium">
                                      Views: {material.views}
                                    </p>
                                  )}
                                  {material.completions && (
                                    <p className="text-sm text-purple-600 font-medium">
                                      Completions: {material.completions}
                                    </p>
                                  )}
                                  {material.uses && (
                                    <p className="text-sm text-orange-600 font-medium">
                                      Uses: {material.uses}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              <div className="mb-4">
                                <div className="flex flex-wrap gap-2">
                                  {material.tags.map((tag, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handlePreview(material)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Preview
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleDownload(material)}
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Download
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleEdit(material)}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleDelete(material)}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {currentUploadTab === 'videos' ? 'Add Video Link' : 'Upload Material'}
            </DialogTitle>
          </DialogHeader>

          {currentUploadTab === 'videos' ? (
            // Video Link Form
            <form onSubmit={handleVideoLinkSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video Name *
                </label>
                <Input
                  value={videoLinkData.name}
                  onChange={(e) => setVideoLinkData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Leadership Training Session"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video Link *
                </label>
                <Input
                  type="url"
                  value={videoLinkData.videoLink}
                  onChange={(e) => setVideoLinkData(prev => ({ ...prev, videoLink: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  YouTube, Vimeo, or any video hosting platform link
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <Textarea
                  value={videoLinkData.description}
                  onChange={(e) => setVideoLinkData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the video content..."
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <Input
                  value={videoLinkData.tags}
                  onChange={(e) => setVideoLinkData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="e.g., Leadership, Training, Communication (comma separated)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Separate tags with commas
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-green-600 hover:bg-green-700"
                >
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Add Video Link
                </Button>
              </div>
            </form>
          ) : (
            // File Upload Form
            <form onSubmit={handleFileUploadSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload File *
                </label>
                {fileUploadData.file ? (
                  <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">{fileUploadData.file.name}</p>
                        <p className="text-sm text-gray-600">
                          {(fileUploadData.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFileUploadData(prev => ({ ...prev, file: null }))}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-400 transition-colors">
                      <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Click to upload file
                      </p>
                      <p className="text-xs text-gray-500">
                        PDF, PPTX, DOCX, XLS (max 50MB)
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.pptx,.ppt,.docx,.doc,.xlsx,.xls"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Material Name *
                </label>
                <Input
                  value={fileUploadData.name}
                  onChange={(e) => setFileUploadData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Leadership Training Slides"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <Textarea
                  value={fileUploadData.description}
                  onChange={(e) => setFileUploadData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the material..."
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <Input
                  value={fileUploadData.tags}
                  onChange={(e) => setFileUploadData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="e.g., Leadership, Workshop, Training (comma separated)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Separate tags with commas
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-green-600 hover:bg-green-700"
                  disabled={!fileUploadData.file}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Material
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyMaterials;